import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { notificationsApi, type NotificationsPage } from "@/api/notifications";
import { infiniteList } from "@/api/paging";
import { queryKeys } from "@/api/queryKeys";

export function useNotifications(enabled = true) {
  return useInfiniteQuery({
    queryKey: queryKeys.notifications,
    ...infiniteList<NotificationsPage["results"][number], NotificationsPage>("/notifications/"),
    enabled,
  });
}

export function useUnreadCount(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.unread,
    queryFn: () => notificationsApi.unreadCount(),
    select: (data) => data.unread_count,
    enabled,
    // Kept fresh without a reload; push messages from the service worker refresh it instantly.
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
    meta: { silent: true },
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, viaPush }: { id: string; viaPush?: boolean }) => notificationsApi.read(id, viaPush),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.notifications }),
    meta: { inlineErrors: true },
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.readAll(),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.notifications }),
  });
}
